import { isDefined } from '@sgme/fp';
import { HStack, Spacer, Tooltip, VStack } from '@sgme/ui';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { type DropEvent, type FileRejection, useDropzone } from 'react-dropzone';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { SgIcon } from '@/components/sg-design-system/components/display/SgIcon';
import { getFileCompactSize } from '@/utils/file.ts';

type UploadFileProps = {
  selectedFile?: File;
  onDrop: <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[], event: DropEvent) => void;
  errorMessage?: string;
  'data-e2e'?: string;
};

export const UploadFile = (props: UploadFileProps) => {
  const { selectedFile, onDrop, errorMessage, 'data-e2e': dataE2e } = props;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const hasError = isDefined(errorMessage);

  // SHOW SELECTED FILE
  if (isDefined(selectedFile)) {
    const { size, unit } = getFileCompactSize(selectedFile.size);

    const fileView = (
      <HStack
        gap="16px"
        verticalAlignment="center"
        horizontalAlignment="start"
        className={clsx('text-info bg-lvl2 px-16px py-8px rounded-3', {
          'border border-danger': hasError,
          'bg-danger-subtle': hasError,
          'text-danger': hasError,
        })}
      >
        <SgIcon icon="upload_file" size="xl" className={hasError ? 'text-danger' : 'text-info'} />

        <VStack>
          <span>{selectedFile.name}</span>
          <span>
            <FormattedNumber value={size} maximumFractionDigits={2} /> <FormattedMessage id={`file.size.${unit}`} />
          </span>
        </VStack>

        <Spacer />

        <span onClick={() => onDrop([], [], new Event(''))}>
          <SgIcon
            icon="close"
            size="md"
            className={clsx('cursor-pointer', {
              'text-danger': hasError,
              'text-light': !hasError,
            })}
          />
        </span>
      </HStack>
    );

    return isDefined(errorMessage) ? (
      <Tooltip contents={errorMessage} color="danger" position="top">
        {fileView}
      </Tooltip>
    ) : (
      fileView
    );
  }

  // SHOW EMPTY DROPZONE
  return (
    <div {...getRootProps()}>
      <input name="upload-file" {...getInputProps()} data-e2e={dataE2e} />

      <HStack
        gap="8px"
        verticalAlignment="center"
        horizontalAlignment="start"
        className={clsx('px-16px py-8px rounded-3', { 'bg-lvl2': isDragActive })}
      >
        <SgIcon icon="attach_file" size="xl" className="text-light" />

        <FormattedMessage id="uploadFile.dragOrSelect" values={{ select: renderSelectLabel }} />
      </HStack>
    </div>
  );
};

const renderSelectLabel = (select: ReactNode) => (
  <label htmlFor="upload-file" className="text-info">
    {select}
  </label>
);
